import { Link, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import axios from '../../lib/axios';
import { useModules } from '../../lib/useModules';
import { formatMoney } from '../../lib/quoteTotals';

const STATUS_META = {
    draft:    { label: 'Borrador',  tone: 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300' },
    sent:     { label: 'Enviada',   tone: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300' },
    accepted: { label: 'Aceptada',  tone: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300' },
    rejected: { label: 'Rechazada', tone: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300' },
    expired:  { label: 'Vencida',   tone: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300' },
};

/**
 * Cotizaciones del contacto en su ficha 360, con el mismo formato que la
 * tarjeta de Negocios. El botón lleva al constructor con el cliente ya
 * seleccionado vía ?contact_id=.
 */
export default function ContactQuotesCard({ contactId }) {
    const navigate = useNavigate();
    const { isModuleEnabled } = useModules();

    const enabled = isModuleEnabled('quotes');

    const { data: resp } = useQuery({
        queryKey: ['contact-quotes', contactId],
        queryFn: () => axios.get('/api/quotes', { params: { contact_id: contactId, per_page: 5 } }),
        // Sin el módulo habilitado el endpoint responde 403: mejor ni pedirlo.
        enabled: enabled && Boolean(contactId),
    });

    if (!enabled) return null;

    const quotes = resp?.data?.data || [];
    const nueva = `/quotes/new?contact_id=${contactId}`;

    if (quotes.length === 0) {
        return (
            <div className="bg-white dark:bg-[#1a100c] rounded-xl shadow-sm border border-gray-100 dark:border-gray-800 p-5 text-center">
                <span className="material-symbols-outlined text-4xl text-gray-300 dark:text-gray-700 mb-2">request_quote</span>
                <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Sin cotizaciones</h3>
                <p className="text-xs text-gray-500 mt-1 mb-3">No hay cotizaciones asociadas a este contacto.</p>
                <button
                    onClick={() => navigate(nueva)}
                    className="text-xs font-medium text-primary hover:underline flex items-center justify-center gap-1 mx-auto"
                >
                    <span className="material-symbols-outlined text-sm">add_circle</span> Agregar Cotización
                </button>
            </div>
        );
    }

    return (
        <div className="bg-white dark:bg-[#1a100c] rounded-xl shadow-sm border border-gray-100 dark:border-gray-800 overflow-hidden">
            <div className="px-4 py-3 border-b border-gray-100 dark:border-gray-800 flex items-center justify-between">
                <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Cotizaciones</h3>
                <button onClick={() => navigate(nueva)} title="Agregar cotización" className="text-primary hover:text-primary/80">
                    <span className="material-symbols-outlined text-lg">add_circle</span>
                </button>
            </div>

            <div className="p-4 flex flex-col gap-3">
                {quotes.map((q) => {
                    const meta = STATUS_META[q.status] || STATUS_META.draft;
                    return (
                        <div
                            key={q.id}
                            onClick={() => navigate(`/quotes/${q.id}`)}
                            className="bg-white dark:bg-[#1a100c] border border-gray-200 dark:border-gray-700 rounded-lg p-3 hover:border-primary/50 transition-colors cursor-pointer group"
                        >
                            <div className="flex justify-between items-start mb-2">
                                <h4 className="text-sm font-semibold text-gray-900 dark:text-white group-hover:text-primary transition-colors">
                                    {q.number}
                                </h4>
                                <span className="text-xs font-bold text-gray-900 dark:text-white">
                                    {formatMoney(q.total, q.currency)}
                                </span>
                            </div>
                            <div className="flex justify-between text-xs text-gray-500 mt-2">
                                <span className={`inline-flex items-center px-2 py-0.5 rounded ${meta.tone}`}>{meta.label}</span>
                                <span>{q.lines_count} {q.lines_count === 1 ? 'ítem' : 'ítems'}</span>
                            </div>
                        </div>
                    );
                })}
            </div>

            <div className="px-4 py-3 bg-gray-50 dark:bg-gray-900/30 text-center border-t border-gray-100 dark:border-gray-800">
                <Link to="/quotes" className="text-xs font-medium text-primary hover:underline">Ver todas las cotizaciones</Link>
            </div>
        </div>
    );
}
